<script setup lang="ts">
  import useVuelidate from "@vuelidate/core"
  import { minValue, required } from "@vuelidate/validators"
  import { IDonationCmsData } from "@/view-models/donationForm.model"
  import NumberInput from "@/components/IcForm/NumberInput.vue"
  import { DonationService } from "@/services/donatie.service"
  import RadioInput from "@/components/IcForm/RadioInput.vue"

  const cmsData = CMSDataProvider.get<IDonationCmsData>(CMS_DATA_ID.DONATION)

  const amount = ref(0.0)
  const selectedPredefinedAmount = ref<number | null>(null)
  const selectedProject = ref<string | null>(null)

  const submitted = ref(false)
  const loading = ref(false)
  const submitSuccess = ref(false)
  const submitFailed = ref(false)

  const projectOptions = computed(() => {
    return cmsData.projects.map(project => ({
      label: project.title,
      value: project.text
    }))
  })

  watch(amount, (newValue) => {
    if (selectedPredefinedAmount.value !== null && newValue !== selectedPredefinedAmount.value) {
      selectedPredefinedAmount.value = null
    }
  })

  const selectAmount = (predefinedAmount: number) => {
    selectedPredefinedAmount.value = predefinedAmount
    amount.value = predefinedAmount
  }

  const onFormSubmit = async () => {
    loading.value = true;
    submitted.value = true;

    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) {
      nextTick(() => {
        document.querySelector(".invalid-feedback")?.scrollIntoView({ block: "center", behavior: "smooth" });
      });

      loading.value = false;
      return;
    }

    try {
      const response = await DonationService.Post({
        amount: amount.value,
        projectDescription: selectedProject.value!,
        returnUrl: cmsData.redirectUrl
      });

      if (response.data) {
        window.location.href = response.data;
      } else {
        submitSuccess.value = true;
      }
    } catch (error) {
      submitFailed.value = true;
    } finally {
      loading.value = false;
    }
  };

  const rules = {
    amount: {
      required,
      minValue: minValue(0), 
    },
    selectedProject: {
      required
    }
  }

  const v$ = useVuelidate(rules, {
    amount,
    selectedProject
  })
</script>

<template>
  <section class="themed-primary container mt-24 pb-24 flex flex-col lg:flex-row">
    <div class="container flex flex-col gap-y-6 px-0 py-20 lg:flex-row lg:gap-20">
      <div class="my-auto gap-x-5 h-fit lg:gap-x-6 lg:w-1/2">
        <h2
          class="themed-newsletter-title mb-4 display--sm md:display--md lg:display--lg 2xl:display--xl 3xl:display--xxl"
        >
          {{ cmsData.title }}
        </h2>
        <form
          class="themed-form mb-4 lg:mb-0"
          @submit.prevent="onFormSubmit"
        >
        <span class="themed-text-input__label font-bold">{{ cmsData.projectsLabel }}</span>
        <div class="ml-4">
          <RadioInput
              id="donation-project"
              v-model="selectedProject"
              name="project-selection"
              :answers="projectOptions"
              :validation="{
                model: v$.selectedProject.$error,
                message: cmsData.requiredErrorMessage,
                submitted: submitted,
              }"
            />
          </div>

          <span class="themed-text-input__label font-bold">{{ cmsData.freeAmountLabel }}</span>
          <div class="ml-4">
            <NumberInput
              id="donation-amount"
              v-model="amount"
              :step="0.01"
              :min="0.01"
              :validation="{
                model: v$.amount.$error,
                message: cmsData.requiredErrorMessage,
                submitted: submitted,
              }"
            />
          </div>


          <div class="mb-4">
            <span class="themed-text-input__label font-bold">{{ cmsData.amountsLabel }}</span>
            <div class="flex flex-wrap gap-2 mt-2 ml-4">
              <button 
                v-for="predefinedAmount in cmsData.predefinedAmounts" 
                :key="predefinedAmount.amount"
                type="button"
                class="rounded-full px-4 py-2 border"
                :class="{ 'bg-primary text-white': selectedPredefinedAmount === predefinedAmount.amount }"
                @click="selectAmount(predefinedAmount.amount)"
              >
                €{{ predefinedAmount.amount }}
              </button>
            </div>
          </div>


          <div
            v-if="submitSuccess"
            class="themed-paragraph paragraph--lg"
          >
            {{ cmsData.submitSuccess }}
          </div>
          <button
            v-else
            type="submit"
            :disabled="loading"
            class="button themed-secondary inverted py-2.5 px-5 flex justify-center w-full lg:w-fit"
          >
            {{ cmsData.paymentButton }}
          </button>

          <div
            v-if="loading"
            class="themed-paragraph paragraph--lg"
          >
            {{ cmsData.loadingLabel }}
          </div>
          <div
            v-else-if="submitFailed"
            class="mt-2 text-red-600 paragraph--lg"
          >
            {{ cmsData.submitError }}
          </div>
        </form>
      </div>
    </div>
  </section>
</template>
